import { useMemo } from 'react';

import { generateNextPaginationCursor } from '@crehana/compass.knowledgehub/utils';

import { useGetCourseDetailUserEnrollmentsByCourseQueryQuery } from './graphql/get-course-detail-user-enrollments-by-course-query.v5.generated';
import type { TCourseDetailUserEnrollment } from './types';
import { transformEnrollmentUser } from './utils/transform-enrollment-user';

type TUseEnrollmentsByCourseProps = {
  courseId: number;
  pageSize: number;
  /**
   * @description The current page to fetch.
   * @default 1
   */
  currentPage?: number;
  searchQuery?: string;
  skip?: boolean;
  apolloConfig?: Partial<{
    /**
     * @description Use this to notify when the network status changes when using `searchQuery` parameter.
     */
    notifyOnNetworkStatusChange: boolean;
  }>;
};

export const useEnrollmentsByCourse = (args: TUseEnrollmentsByCourseProps) => {
  const {
    skip,
    courseId,
    pageSize,
    apolloConfig,
    currentPage = 1,
    searchQuery: search,
  } = args;
  const { data, loading, error } =
    useGetCourseDetailUserEnrollmentsByCourseQueryQuery({
      context: {
        clientName: 'v5',
      },
      variables: {
        input: {
          course_id: courseId,
          pagination: {
            first: pageSize,
            after: generateNextPaginationCursor(currentPage - 1, pageSize),
          },
          search: search ? { query: search } : undefined,
        },
      },
      notifyOnNetworkStatusChange: apolloConfig?.notifyOnNetworkStatusChange,
      skip,
    });

  const enrollments = useMemo<TCourseDetailUserEnrollment[]>(() => {
    if (
      data?.enrollments.get_users_by_assigned_courses_with_search.__typename ===
      'UserCourseInfoConnection'
    ) {
      return (
        data.enrollments.get_users_by_assigned_courses_with_search.edges?.map(
          edge => transformEnrollmentUser(edge.node),
        ) ?? []
      );
    }

    return [];
  }, [data?.enrollments.get_users_by_assigned_courses_with_search]);

  const isEmptyEnrollments = enrollments.length === 0;
  const totalEnrollments =
    data?.enrollments.get_users_by_assigned_courses_with_search.__typename ===
    'UserCourseInfoConnection'
      ? (data.enrollments.get_users_by_assigned_courses_with_search
          .total_count ?? 0)
      : 0;
  const totalPages = Math.ceil(totalEnrollments / pageSize);

  return {
    isEmptyEnrollments,
    totalEnrollments,
    enrollments,
    totalPages,
    loading,
    error,
  };
};
